import React, { useMemo, useState } from 'react';
import { Card, Empty, Select } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import { useNotifications } from '../hooks/useNotifications';
import { TimeRangeLinks } from '../ui/TimeRangeLinks';
import { formatCurrency } from '../utils/numbers';

type Period = 'day' | 'week' | 'month' | 'year' | 'all';
type Props = { height?: number; topN?: number };

const D = 24 * 60 * 60 * 1000;
const rangeFrom = (p: Period): number => {
  const now = Date.now();
  switch (p) {
    case 'day':   return now - D;
    case 'week':  return now - 7 * D;
    case 'month': return now - 30 * D;
    case 'year':  return now - 365 * D;
    case 'all':   return 0;
  }
};

const MEDAL = ['#FFD700', '#C0C0C0', '#CD7F32'];
const REST   = '#ff7a45';
const barColor = (i: number) => MEDAL[i] ?? REST;

const fmtAMD = (v: number) => formatCurrency(v, 'AMD');

interface BuyerRow {
  name: string;
  orders: number;
  revenue: number;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload as BuyerRow;
  return (
    <div style={{
      background: '#fff', border: '1px solid #eee', borderRadius: 8,
      padding: '8px 12px', boxShadow: '0 4px 16px rgba(0,0,0,.08)',
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{d.name}</div>
      <div style={{ color: '#555' }}>
        Заказов: <strong style={{ color: '#ff7a45' }}>{d.orders}</strong>
      </div>
      <div style={{ color: '#555' }}>
        Выручка: <strong style={{ color: '#52c41a' }}>{fmtAMD(d.revenue)}</strong>
      </div>
    </div>
  );
};

const TopBuyersChart: React.FC<Props> = ({ height = 400, topN = 10 }) => {
  const { items } = useNotifications(10_000);
  const [period, setPeriod] = useState<Period>('month');
  const [top,    setTop]    = useState<number>(10);

  const data = useMemo(() => {
    const from = rangeFrom(period);
    const buyers = new Map<string, BuyerRow>();

    for (const n of items) {
      const t = new Date(n.createdAt).getTime();
      if (!Number.isFinite(t) || t < from) continue;
      if (n.status === 'canceled' || n.status === 'returned') continue;

      const key = n.phone || n.customer;
      // Анонимные продажи на точке (ref вида POS-…) не считаем за клиента —
      // у них нет реального телефона как уникального идентификатора.
      if (!key || /^POS-/i.test(String(key))) continue;
      const existing = buyers.get(key);
      if (existing) {
        existing.orders += 1;
        existing.revenue += Number(n.total) || 0;
      } else {
        buyers.set(key, { name: n.customer, orders: 1, revenue: Number(n.total) || 0 });
      }
    }

    return Array.from(buyers.values())
      .sort((a, b) => b.orders - a.orders)
      .slice(0, top);
  }, [items, period, top]);

  const dynamicHeight = Math.max(height, data.length * 44 + 80);

  const TOP_OPTIONS = [
    { value: 10, label: 'Топ 10' },
    { value: 20, label: 'Топ 20' },
    { value: 30, label: 'Топ 30' },
  ];

  return (
    <Card
      variant="borderless"
      className="stat-card stat-card--accent-blue"
      styles={{ header: { paddingTop: 10, paddingBottom: 8, alignItems: 'center' } }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="stat-card__icon" style={{ display: 'grid', placeItems: 'center' }}>
            <TeamOutlined />
          </span>
          <span className="stat-card__title">Лучший покупатель</span>
        </div>
      }
      extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Select
            size="small"
            value={top}
            onChange={setTop}
            options={TOP_OPTIONS}
            style={{ width: 95 }}
            popupMatchSelectWidth={false}
          />
          <TimeRangeLinks value={period} onChange={setPeriod} withAll />
        </div>
      }
    >
      <div style={{ width: '100%', height: dynamicHeight }}>
        {!data.length ? (
          <Empty description="Нет данных" />
        ) : (
          <ResponsiveContainer>
            <BarChart
              layout="vertical"
              data={data}
              margin={{ left: 8, right: 40, top: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="name"
                width={130}
                tick={{ fontSize: 12, fontWeight: 500 }}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,122,69,0.06)' }} />
              <Bar dataKey="orders" radius={[0, 4, 4, 0]} maxBarSize={28}>
                {data.map((_, i) => (
                  <Cell key={i} fill={barColor(i)} />
                ))}
                <LabelList
                  dataKey="orders"
                  position="right"
                  style={{ fontSize: 12, fontWeight: 600, fill: '#333' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default TopBuyersChart;
